// ENREGISTRER UN ENTRETIEN AVEC SON TÉLÉPHONE — l'enregistreur du navigateur
// (MediaRecorder), sans application à installer.
//
// Enregistrer · Pause · Arrêter, puis Réécouter et Envoyer. Pendant
// l'enregistrement, l'écran reste allumé (quand le navigateur le permet) et ce
// qui est capté est recopié dans le téléphone toutes les 15 secondes : un envoi
// raté, ou un onglet fermé par erreur, ne fait pas perdre l'entretien.
//
// Limite d'une application web : verrouiller le téléphone ou passer à une
// autre application coupe le micro (surtout sur iPhone). On le dit avant de
// commencer, et ce qui a été capté jusque-là est gardé.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, ErrorBanner } from '../components/ui';
import { clearPending, loadPending, savePending, type PendingCapture } from './pending';
import './capture.css';

/** Formats essayés dans l'ordre : Opus en WebM (Chrome, Android), puis MP4/AAC (Safari, iPhone). */
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm', 'audio/ogg;codecs=opus'];

export function recorderSupported(): boolean {
  return typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
}

function pickMime(): string {
  for (const m of MIME_CANDIDATES) if (MediaRecorder.isTypeSupported?.(m)) return m;
  return '';
}

function extOf(type: string): string {
  if (type.includes('mp4') || type.includes('aac')) return 'm4a';
  if (type.includes('ogg')) return 'ogg';
  return 'webm';
}

export function formatDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function captureName(type: string): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `entretien-${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}-${p(d.getHours())}h${p(d.getMinutes())}.${extOf(type)}`;
}

type Phase = 'idle' | 'starting' | 'recording' | 'paused' | 'done';

interface WakeLockLike { release: () => Promise<void> }

export function VoiceRecorder({
  storageKey,
  disabled = false,
  busy = false,
  onSend,
}: {
  /** Clé de la copie gardée dans le téléphone (l'identifiant de l'entretien). */
  storageKey: string;
  /** Tant que l'accord de la personne n'est pas attesté, on n'enregistre pas. */
  disabled?: boolean;
  /** Envoi en cours (barre de progression tenue par le parent). */
  busy?: boolean;
  /** Envoie le fichier ; `true` quand le serveur l'a bien reçu. */
  onSend: (file: File) => Promise<boolean>;
}) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [capture, setCapture] = useState<PendingCapture | null>(null);
  const [restored, setRestored] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const rec = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const mime = useRef('');
  // Le chronomètre : le temps cumulé avant la dernière pause, et le départ du segment.
  const before = useRef(0);
  const segment = useRef(0);
  const timers = useRef<number[]>([]);
  const raf = useRef(0);
  const audio = useRef<AudioContext | null>(null);
  const wake = useRef<WakeLockLike | null>(null);
  const interrupted = useRef(false);

  const now = () => before.current + (segment.current ? performance.now() - segment.current : 0);
  const typeNow = () => (rec.current?.mimeType || mime.current || 'audio/webm').split(';')[0] || 'audio/webm';
  const build = useCallback((): PendingCapture => {
    const type = typeNow();
    return { blob: new Blob(chunks.current, { type }), type, name: captureName(type), durationMs: now(), savedAt: Date.now() };
  }, []);

  // Une capture restée dans le téléphone (envoi raté, onglet fermé) : on la propose.
  useEffect(() => {
    let alive = true;
    void loadPending(storageKey).then((p) => {
      if (!alive || !p || p.blob.size === 0) return;
      setCapture(p);
      setRestored(true);
      setPhase('done');
    });
    return () => {
      alive = false;
    };
  }, [storageKey]);

  // Lecteur pour réécouter : une adresse locale, libérée quand elle change.
  useEffect(() => {
    if (!capture) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(capture.blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [capture]);

  const requestWake = useCallback(async () => {
    try {
      const nav = navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<WakeLockLike> } };
      wake.current = (await nav.wakeLock?.request('screen')) ?? null;
    } catch {
      /* refusé (batterie faible, navigateur) : l'enregistrement continue */
    }
  }, []);

  const cleanup = useCallback(() => {
    timers.current.forEach((t) => window.clearInterval(t));
    timers.current = [];
    cancelAnimationFrame(raf.current);
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    void audio.current?.close().catch(() => undefined);
    audio.current = null;
    void wake.current?.release().catch(() => undefined);
    wake.current = null;
    setLevel(0);
  }, []);

  // Écran rallumé : le verrou d'écran saute quand on quitte l'onglet, on le reprend.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && rec.current?.state === 'recording' && !wake.current) void requestWake();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [requestWake]);

  // Fermer l'onglet en plein enregistrement : le navigateur demande confirmation.
  useEffect(() => {
    if (phase !== 'recording' && phase !== 'paused') return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [phase]);

  // Quitter l'écran en plein enregistrement : on arrête proprement, la copie reste.
  useEffect(() => () => {
    if (rec.current && rec.current.state !== 'inactive') rec.current.stop();
    cleanup();
  }, [cleanup]);

  async function start() {
    setError(null);
    setNotice(null);
    setRestored(false);
    setPhase('starting');
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      stream.current = s;
      mime.current = pickMime();
      const r = new MediaRecorder(s, mime.current ? { mimeType: mime.current, audioBitsPerSecond: 64000 } : undefined);
      rec.current = r;
      chunks.current = [];
      before.current = 0;
      segment.current = performance.now();
      interrupted.current = false;
      r.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };
      r.onstop = () => {
        if (segment.current) before.current += performance.now() - segment.current;
        segment.current = 0;
        const c = build();
        cleanup();
        rec.current = null;
        if (c.blob.size === 0) {
          setPhase('idle');
          setError('Rien n’a été capté. Vérifiez que le micro est autorisé, puis recommencez.');
          return;
        }
        void savePending(storageKey, c);
        setCapture(c);
        setPhase('done');
        if (interrupted.current) {
          setNotice(`L’enregistrement s’est interrompu à ${formatDuration(c.durationMs)} (téléphone verrouillé ou micro pris par une autre application). Ce qui a été capté est gardé.`);
        }
      };
      // Micro coupé par le système : on garde ce qui a été capté.
      s.getAudioTracks()[0]?.addEventListener('ended', () => {
        interrupted.current = true;
        if (r.state !== 'inactive') r.stop();
      });
      r.start(1000);
      setPhase('recording');
      void requestWake();

      timers.current.push(window.setInterval(() => setElapsed(now()), 250));
      timers.current.push(window.setInterval(() => {
        if (chunks.current.length) void savePending(storageKey, build());
      }, 15000));

      // Niveau du micro : on voit tout de suite si la voix est captée.
      try {
        const ctx = new AudioContext();
        audio.current = ctx;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        ctx.createMediaStreamSource(s).connect(analyser);
        const data = new Uint8Array(analyser.fftSize);
        const loop = () => {
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (const v of data) sum += ((v - 128) / 128) ** 2;
          setLevel(Math.min(1, Math.sqrt(sum / data.length) * 4));
          raf.current = requestAnimationFrame(loop);
        };
        loop();
      } catch {
        /* pas d'indicateur de niveau : l'enregistrement, lui, fonctionne */
      }
    } catch (e) {
      cleanup();
      setPhase('idle');
      const name = (e as { name?: string })?.name;
      setError(name === 'NotAllowedError'
        ? 'Le micro a été refusé. Autorisez-le dans les réglages du navigateur (cadenas à côté de l’adresse), puis recommencez.'
        : name === 'NotFoundError'
          ? 'Aucun micro trouvé sur cet appareil.'
          : 'Le micro n’a pas pu démarrer. Fermez les autres applications qui l’utilisent, puis recommencez.');
    }
  }

  function pause() {
    if (rec.current?.state !== 'recording') return;
    rec.current.pause();
    before.current += performance.now() - segment.current;
    segment.current = 0;
    setPhase('paused');
  }

  function resume() {
    if (rec.current?.state !== 'paused') return;
    rec.current.resume();
    segment.current = performance.now();
    setPhase('recording');
  }

  function stop() {
    if (rec.current && rec.current.state !== 'inactive') rec.current.stop();
  }

  async function send() {
    if (!capture) return;
    setError(null);
    const ok = await onSend(new File([capture.blob], capture.name, { type: capture.type }));
    if (ok) {
      await clearPending(storageKey);
      setCapture(null);
      setRestored(false);
      setNotice(null);
      setPhase('idle');
      setElapsed(0);
    }
  }

  async function discard() {
    await clearPending(storageKey);
    setCapture(null);
    setConfirmDiscard(false);
    setRestored(false);
    setNotice(null);
    setPhase('idle');
    setElapsed(0);
  }

  if (!recorderSupported()) {
    return <p className="card-note">Ce navigateur ne sait pas enregistrer le son. Utilisez « Filmer » ou envoyez un fichier.</p>;
  }

  const live = phase === 'recording' || phase === 'paused';

  return (
    <div className={`recorder${live ? ' is-live' : ''}`}>
      {phase === 'idle' || phase === 'starting' ? (
        <>
          <Button variant="accent" className="recorder-main" onClick={() => void start()} disabled={disabled} loading={phase === 'starting'}>
            <span className="recorder-dot" aria-hidden="true" /> Enregistrer la voix
          </Button>
          <p className="zy-field__aide">
            Gardez Zygosight ouvert et l’écran allumé : verrouiller le téléphone ou changer d’application coupe le micro.
            Ce qui a été capté est gardé dans le téléphone jusqu’à l’envoi.
          </p>
        </>
      ) : null}

      {live ? (
        <>
          <div className="recorder-clock" role="timer" aria-live="off">
            <span className={`recorder-dot${phase === 'recording' ? ' is-on' : ''}`} aria-hidden="true" />
            {formatDuration(elapsed)}
            <span className="recorder-state">{phase === 'recording' ? 'Enregistrement' : 'En pause'}</span>
          </div>
          <div className="recorder-level" aria-hidden="true">
            <span style={{ transform: `scaleX(${phase === 'recording' ? Math.max(0.03, level) : 0.03})` }} />
          </div>
          <div className="recorder-actions">
            {phase === 'recording' ? (
              <Button variant="ghost" onClick={pause}>Pause</Button>
            ) : (
              <Button variant="ghost" onClick={resume}>Reprendre</Button>
            )}
            <Button variant="accent" onClick={stop}>Arrêter</Button>
          </div>
        </>
      ) : null}

      {phase === 'done' && capture ? (
        <>
          {restored ? (
            <p className="card-note">
              Un enregistrement du {new Date(capture.savedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} n’a pas
              encore été envoyé. Il est gardé dans ce téléphone.
            </p>
          ) : null}
          {notice ? <p className="card-note recorder-notice">{notice}</p> : null}
          {url ? <audio className="recording-player" controls preload="metadata" src={url} /> : null}
          <p className="zy-field__aide">
            {formatDuration(capture.durationMs)} ·{' '}
            {capture.blob.size >= 1024 * 1024
              ? `${(capture.blob.size / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`
              : `${Math.max(1, Math.round(capture.blob.size / 1024))} Ko`}
          </p>
          <div className="recorder-actions">
            <Button variant="accent" onClick={() => void send()} loading={busy} disabled={disabled}>
              Envoyer et transcrire
            </Button>
            {confirmDiscard ? (
              <>
                <Button variant="ghost" onClick={() => setConfirmDiscard(false)}>Garder</Button>
                <Button variant="ghost" onClick={() => void discard()}>Supprimer définitivement</Button>
              </>
            ) : (
              <Button variant="ghost" onClick={() => setConfirmDiscard(true)} disabled={busy}>Recommencer</Button>
            )}
          </div>
          {disabled ? <p className="zy-field__aide">Cochez l’accord de la personne enregistrée pour envoyer.</p> : null}
        </>
      ) : null}

      {error ? <ErrorBanner>{error}</ErrorBanner> : null}
    </div>
  );
}
